import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { USER_ID_PLACEHOLDER } from '../auth/user-id.interceptor';
import { Charger, ChargerAddResult, ChargerConfigOcppCpmsResponse, ChargerDeleteResult, ChargerDiagnostics, ChargerOverview, ChargerSettingsChargeSchedule, ChargerSettingsChargeScheduleResponse, ChargerSettingsInstaller, ChargerSettingsInstallerDso, ChargerSettingsInstallerDsoResponse, ChargerSettingsInstallerOcpp, ChargerSettingsInstallerOcppResponse, ChargerSettingsInstallerResponse, ChargerSettingsPutResult, ChargerSettingsSolar, ChargerSettingsSolarResponse, ChargerSettingsUser, ChargerSettingsUserResponse, ChargersOverviewResponse, ChargersResponse, Cpms } from '../models';

@Injectable({
	providedIn: 'root'
})
export class ChargersService {
	private http = inject(HttpClient);

	constructor() { }

	getChargers(): Observable<Charger[]> {
		return this.http.get<ChargersResponse>(`${environment.api}/users/${USER_ID_PLACEHOLDER}/chargers`).pipe(map((chargersResponse) => chargersResponse.chargers));
	}

	getChargersOverview(): Observable<ChargerOverview[]> {
		return this.http
			.get<ChargersOverviewResponse>(`${environment.api}/users/${USER_ID_PLACEHOLDER}/chargers/status?id=overview`)
			.pipe(
				map((chargersOverviewResponse) => chargersOverviewResponse.chargers),
				catchError((error: HttpErrorResponse) => {
					if (error.status === 404) {
						return of([]);
					}
					return throwError(() => error);
				})
			);
	}

	putChargerAdd(serialNumber: string, bluetoothPasskey: string): Observable<ChargerAddResult> {
		let body = {
			registrationInfo: {
				serialNumber: serialNumber,
				bluetoothPasskey: bluetoothPasskey
			}
		};
		return this.http.put<void>(
			`${environment.api}/users/${USER_ID_PLACEHOLDER}/chargers/${serialNumber}`, 
			body, 
			{ observe: 'response' }
		).pipe(
			map(response => ({ success: (response.status === 200) })),
			catchError(err => {
				console.error('PUT failed:', err);
				return of({ success: false, errorMessage: `${err.error.errorMessage} (${err.status})` });
			})
		);
	}

	deleteCharger(serialNumber: string): Observable<ChargerDeleteResult> {
		return this.http.delete<void>(
			`${environment.api}/users/${USER_ID_PLACEHOLDER}/chargers/${serialNumber}`, 
			{ observe: 'response' }
		).pipe(
			map(response => ({ success: (response.status === 200) })),
			catchError(err => {
				console.error('DELETE failed:', err);
				return of({ success: false, errorMessage: `${err.error.errorMessage} (${err.status})` });
			})
		);
	}

	getChargerOverview(serialNumber: string): Observable<ChargerOverview> {
		return this.http.get<ChargerOverview>(`${environment.api}/users/${USER_ID_PLACEHOLDER}/chargers/${serialNumber}/status?id=overview`).pipe();
	}

	getChargerDiagnostics(serialNumber: string): Observable<ChargerDiagnostics> {
		return this.http.get<ChargerDiagnostics>(`${environment.api}/users/${USER_ID_PLACEHOLDER}/chargers/${serialNumber}/status?id=diagnostics`).pipe();
	}

	getChargerSettingsUser(serialNumber: string): Observable<ChargerSettingsUser> {
		return this.http.get<ChargerSettingsUserResponse>(`${environment.api}/users/${USER_ID_PLACEHOLDER}/chargers/${serialNumber}/settings?id=user`).pipe(map((chargerSettingsUserResponse) => chargerSettingsUserResponse.userSettings));
	}

	putChargerSettingsUser(serialNumber: string, id: string, value: any): Observable<ChargerSettingsPutResult> {
		return this.putChargerSettings(serialNumber, 'user', { userSettings: { [id]: value }});
	}

	getChargerSettingsSolar(serialNumber: string): Observable<ChargerSettingsSolar> {
		return this.http.get<ChargerSettingsSolarResponse>(`${environment.api}/users/${USER_ID_PLACEHOLDER}/chargers/${serialNumber}/settings?id=solar`).pipe(map((chargerSettingsSolarResponse) => chargerSettingsSolarResponse.solarSettings));
	}

	putChargerSettingsSolar(serialNumber: string, id: string, value: any): Observable<ChargerSettingsPutResult> {
		return this.putChargerSettings(serialNumber, 'solar', { solarSettings: { [id]: value }});
	}

	getChargerSettingsChargeSchedule(serialNumber: string): Observable<ChargerSettingsChargeSchedule> {
		return this.http.get<ChargerSettingsChargeScheduleResponse>(`${environment.api}/users/${USER_ID_PLACEHOLDER}/chargers/${serialNumber}/settings?id=chargeSchedule`).pipe(map((chargerSettingsChargeScheduleResponse) => chargerSettingsChargeScheduleResponse.chargeScheduleSettings));
	}

	putChargerSettingsChargeSchedule(serialNumber: string, id: string, value: any): Observable<ChargerSettingsPutResult> {
		if (id === 'weekSchedule') {
			return this.putChargerSettings(serialNumber, 'chargeSchedule', { chargeScheduleSettings: { enabled: true, scheduleType: 'WeekSchedule', weekSchedule: value }});
		} else {
			return this.putChargerSettings(serialNumber, 'chargeSchedule', { chargeScheduleSettings: { [id]: value }});
		}
	}

	getChargerSettingsInstaller(serialNumber: string): Observable<ChargerSettingsInstaller> {
		return this.http.get<ChargerSettingsInstallerResponse>(`${environment.api}/users/${USER_ID_PLACEHOLDER}/chargers/${serialNumber}/settings?id=installer`).pipe(map((chargerSettingsInstallerResponse) => chargerSettingsInstallerResponse.installerSettings));
	}

	putChargerSettingsInstaller(serialNumber: string, id: string, value: any): Observable<ChargerSettingsPutResult> {
		return this.putChargerSettings(serialNumber, 'installer', { installerSettings: { [id]: value }});
	}

	getChargerSettingsInstallerDso(serialNumber: string): Observable<ChargerSettingsInstallerDso> {
		return this.http.get<ChargerSettingsInstallerDsoResponse>(`${environment.api}/users/${USER_ID_PLACEHOLDER}/chargers/${serialNumber}/settings?id=installerDso`).pipe(map((chargerSettingsInstallerDsoResponse) => chargerSettingsInstallerDsoResponse.installerDsoSettings));
	}

	putChargerSettingsInstallerDso(serialNumber: string, id: string, value: any): Observable<ChargerSettingsPutResult> {
		return this.putChargerSettings(serialNumber, 'installerDso', { installerDsoSettings: { [id]: value }});
	}

	getChargerSettingsInstallerOcpp(serialNumber: string): Observable<ChargerSettingsInstallerOcpp> {
		return this.http.get<ChargerSettingsInstallerOcppResponse>(`${environment.api}/users/${USER_ID_PLACEHOLDER}/chargers/${serialNumber}/settings?id=installerOcpp`).pipe(map((chargerSettingsInstallerOcppResponse) => chargerSettingsInstallerOcppResponse.installerOcppSettings));
	}

	putChargerSettingsInstallerOcpp(serialNumber: string, id: string, value: any): Observable<ChargerSettingsPutResult> {
		return this.putChargerSettings(serialNumber, 'installerOcpp', { installerOcppSettings: { [id]: value }});
	}

	putChargerSettings(serialNumber: string, id: string, setting: any): Observable<ChargerSettingsPutResult> {
		setting.transactionId = '1';
		console.log('putChargerSettings:', setting);
		return this.http.put<void>(
			`${environment.api}/users/${USER_ID_PLACEHOLDER}/chargers/${serialNumber}/settings?id=${id}`, 
			setting, 
			{ observe: 'response' }
		).pipe(
			map(response => ({ success: (response.status === 200) })),
			catchError(err => {
				console.error('PUT failed:', err);
				return of({ success: false, errorMessage: `${err.error.errorMessage} (${err.status})` });
			})
		);
	}


	putChargerCommand(serialNumber: string, id: string, parameters?: object): Observable<ChargerSettingsPutResult> {
		let body = {
			transactionId: '1',
			...parameters
		};
		return this.http.put<void>(
			`${environment.api}/users/${USER_ID_PLACEHOLDER}/chargers/${serialNumber}/command?id=${id}`, 
			body, 
			{ observe: 'response' }
		).pipe(
			map(response => ({ success: (response.status === 200) })),
			catchError(err => {
				console.error('PUT failed:', err);
				return of({ success: false, errorMessage: `${err.error.errorMessage} (${err.status})` });
			})
		);
	}


	getChargerConfigOcppCpms(serialNumber: string): Observable<Cpms[]> {
		return this.http.get<ChargerConfigOcppCpmsResponse>(`${environment.api}/users/${USER_ID_PLACEHOLDER}/chargers/${serialNumber}/config/ocpp/charge-point-management-systems`).pipe(map((chargerConfigOcppCpmsResponse) => chargerConfigOcppCpmsResponse.cpmsList));
	}

}
